import React, { useState, useCallback, FC } from 'react'

import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { logBridgeError, sendSelectContent } from 'common/helpers'
import { Routes } from 'common/constants'

import { Text } from '@interco/inter-ui/components/Text'

import { useGetFAQAnswer } from '../../hooks'
import { QuestionsDetailsSkeleton } from '../questions-details-skeleton'
import { Article } from '../article'
import { ChevronDown, QuestionArea, FrequentQuestionsAccordion, Clickable } from './styles'

type FrequestQuestionProps = {
  id: string
  question: string
}

export type Answer = {
  category_id: string
  content: string
  created_at: string
  id: string
  language: string
  title: string
  updated_at: string
}

export const FaqCard: FC<FrequestQuestionProps> = ({ id, question }) => {
  const { userLanguage, userSelectedEntity } = useAppContext()

  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const { isAnswerLoading, isAnswerError, answer, fetchFAQAnswer } = useGetFAQAnswer({
    id,
    language: userLanguage ?? 'pt-BR',
    userSelectedEntity: userSelectedEntity ?? 'BR',
  })

  const onExpandClick = useCallback(async () => {
    const current = isExpanded
    const next = !current

    try {
      await sendSelectContent({
        flow: 'frequent_questions',
        step: 'frequent_questions',
        screen_name: Routes.FREQUENT_QUESTIONS,
        element_name: 'expandFrequentQuestion',
        element_type: 'button',
        parent_name: 'FrequentQuestions',
        scenario: JSON.stringify({
          screen: Routes.FREQUENT_QUESTIONS,
          product: userSelectedEntity,
          prevState: `${current}`,
          currState: `${next}`,
          ref_type: 'button',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send frequent questions tic', error)
    }

    setIsExpanded(() => {
      const nextExpanded = !current

      if (nextExpanded && !answer) fetchFAQAnswer()
      return !current
    })
  }, [answer, fetchFAQAnswer, isExpanded, userSelectedEntity])

  const isQuestionEndedWithPonctuation =
    question.endsWith('?') || question.endsWith('.') || question.endsWith('!')

  return (
    <FrequentQuestionsAccordion
      labelComponent={
        <Clickable>
          <QuestionArea onClick={onExpandClick} $isExpanded={isExpanded}>
            <Text variant="body-3" className={isExpanded ? 'expanded' : 'contracted'} bold>
              {isQuestionEndedWithPonctuation ? question : `${question}.`}
            </Text>
            <ChevronDown
              className={isExpanded ? 'up' : 'down'}
              height={28}
              width={28}
              color="var(--secondary500)"
            />
          </QuestionArea>
        </Clickable>
      }
      expanded={isExpanded}
      onClick={() => null}
    >
      {!isAnswerLoading ? (
        <Article htmlText={answer?.content ?? ''} isError={isAnswerError} />
      ) : (
        <QuestionsDetailsSkeleton />
      )}
    </FrequentQuestionsAccordion>
  )
}
